import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Tooltip from '../../../componentLibrary/Tooltip';
import handleKeyPress from '../../../common/handleKeyPress';
import * as Styled from './styles';
import images from '../../../constants/imageSources/imageSources';

// have to wrap the mouse down functions around this in order to prevent mouseDown bubbling up to onFocus and making the 'select block' label always visible
const onButtonMouseDown = (e, cb) => {
    e.preventDefault();
    e.stopPropagation();
    cb(e);
};

const ButtonRemote = ({ disabled, method, remoteUserName, type, altText }) => (
    <Tooltip
        id={`${remoteUserName}-selected-${type}`}
        disableTooltip={!disabled}
        text={`${type} is unavailable while ${remoteUserName} is editing an element in this block.`}
    >
        <Styled.Button
            role="button"
            tabIndex="0"
            alt={altText}
            aria-pressed={type === 'Lock' && false}
            aria-disabled={disabled}
            onMouseDown={(e) => !disabled && onButtonMouseDown(e, method)}
            onKeyDown={(e) => handleKeyPress(e, !disabled && (() => method(e)))}
        >
            <Styled.Icon
                disabled={disabled}
                className="material-icons"
                alt={`${altText} icon`}
                isDelete
                aria-hidden
                aria-disabled={disabled}
            >
                {type === 'Lock' ? 'lock_open' : type.toLowerCase()}
            </Styled.Icon>
        </Styled.Button>
    </Tooltip>
);

ButtonRemote.propTypes = {
    disabled: PropTypes.bool.isRequired,
    method: PropTypes.func.isRequired,
    remoteUserName: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    altText: PropTypes.string.isRequired,
};

const CanvasBlockControls = ({
    isMouseOverCanvasBlock,
    isCurrBlockSelected,
    blockContainsRemoteSelectedElement,
    remoteUser,
    structureLocked,
    switchLockStructure,
    switchCheckBox,
    moveUpBlock,
    moveDownBlock,
    duplicateBlock,
    deleteBlock,
}) => {
    const [hasFocus, setHasFocus] = useState(false);
    const [isHover, setIsHover] = useState(false);

    if (!isMouseOverCanvasBlock && !isCurrBlockSelected) {
        return null;
    }

    return (
        <Styled.ControlsWrapper
            onFocus={() => setHasFocus(true)}
            onBlur={() => setHasFocus(false)}
            onMouseOver={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            <Styled.LockIconsWrapper>
                <Styled.LockButtons>
                    {!structureLocked && (
                        <>
                            <ButtonRemote
                                remoteUserName={`${remoteUser.firstName} ${remoteUser.lastName}`}
                                disabled={blockContainsRemoteSelectedElement}
                                method={switchLockStructure}
                                type="Lock"
                                altText="Lock block"
                            />
                        </>
                    )}
                    {structureLocked && (
                        <>
                            <Styled.LockIconsDiv
                                role="button"
                                tabIndex="0"
                                alt="Unlock block"
                                aria-pressed
                                onMouseDown={(e) => onButtonMouseDown(e, switchLockStructure)}
                                onKeyDown={(e) => handleKeyPress(e, () => switchLockStructure(e))}
                            >
                                <Styled.Icon
                                    className="material-icons"
                                    alt="Unlock block icon"
                                    style={{ color: 'red' }}
                                    aria-hidden
                                >
                                    lock
                                </Styled.Icon>
                            </Styled.LockIconsDiv>
                        </>
                    )}
                </Styled.LockButtons>
            </Styled.LockIconsWrapper>

            {!structureLocked && (
                <Styled.CheckboxIconsWrapper>
                    <Styled.CheckboxLabel>
                        <Styled.Button
                            role="checkbox"
                            tabIndex="0"
                            alt={`${isCurrBlockSelected ? 'Unselect' : 'Select'} block`}
                            aria-checked={isCurrBlockSelected}
                            onMouseDown={(e) => onButtonMouseDown(e, switchCheckBox)}
                            onKeyDown={(e) => handleKeyPress(e, () => switchCheckBox(e))}
                        >
                            {isCurrBlockSelected ? (
                                <Styled.Icon
                                    className="material-icons"
                                    alt="Unselect block icon"
                                    style={{ color: '#0277BD' }}
                                    aria-hidden
                                >
                                    check_box
                                </Styled.Icon>
                            ) : (
                                <Styled.Icon className="material-icons" alt="Select block icon">
                                    check_box_outline_blank
                                </Styled.Icon>
                            )}
                        </Styled.Button>
                        <Styled.CheckboxText isVisible={isHover || hasFocus}>select block</Styled.CheckboxText>
                    </Styled.CheckboxLabel>
                </Styled.CheckboxIconsWrapper>
            )}

            {!structureLocked && (
                <>
                    <Styled.RightControls>
                        <Styled.Button
                            role="button"
                            tabIndex="0"
                            alt="Move block up"
                            onMouseDown={(e) => onButtonMouseDown(e, moveUpBlock)}
                            onKeyDown={(e) => handleKeyPress(e, () => moveUpBlock(e))}
                        >
                            <Styled.Icon className="material-icons" alt="Move block up icon" aria-hidden>
                                arrow_upward
                            </Styled.Icon>
                        </Styled.Button>
                        <Styled.Button
                            role="button"
                            tabIndex="0"
                            alt="Move block down"
                            onMouseDown={(e) => onButtonMouseDown(e, moveDownBlock)}
                            onKeyDown={(e) => handleKeyPress(e, () => moveDownBlock(e))}
                        >
                            <Styled.Icon className="material-icons" alt="Move block down" aria-hidden>
                                arrow_downward
                            </Styled.Icon>
                        </Styled.Button>
                        <Styled.Button
                            role="button"
                            tabIndex="0"
                            alt="Copy block"
                            onMouseDown={(e) => onButtonMouseDown(e, duplicateBlock)}
                            onKeyDown={(e) => handleKeyPress(e, () => duplicateBlock(e))}
                        >
                            <img
                                src={images.ICONS.COPY_BLACK}
                                style={{ width: 16, cursor: 'pointer' }}
                                alt="Copy block icon"
                                aria-hidden
                            />
                        </Styled.Button>

                        <ButtonRemote
                            remoteUserName={`${remoteUser.firstName} ${remoteUser.lastName}`}
                            disabled={blockContainsRemoteSelectedElement}
                            method={deleteBlock}
                            type="Delete"
                            altText="Delete block"
                        />
                    </Styled.RightControls>
                </>
            )}
        </Styled.ControlsWrapper>
    );
};

CanvasBlockControls.propTypes = {
    isMouseOverCanvasBlock: PropTypes.bool.isRequired,
    isCurrBlockSelected: PropTypes.bool.isRequired,
    blockContainsRemoteSelectedElement: PropTypes.bool.isRequired,
    remoteUser: PropTypes.object.isRequired,
    structureLocked: PropTypes.bool.isRequired,
    switchLockStructure: PropTypes.func.isRequired,
    switchCheckBox: PropTypes.func.isRequired,
    moveUpBlock: PropTypes.func.isRequired,
    moveDownBlock: PropTypes.func.isRequired,
    duplicateBlock: PropTypes.func.isRequired,
    deleteBlock: PropTypes.func.isRequired,
};

export default CanvasBlockControls;
